import { FC, useEffect, useMemo } from "react";
import { builtinFormatters, logger } from "cayo.ui";
import { UrlPollingManager } from "@cayo/admin-components/components/alerts/url-pooling-manager";
import { requestStatusToSeverity } from "@cayo/admin-components/components/alerts/utils";
import { alertDispatcher, IUpdateNewAlerts } from "@cayo/shared/components/Alerts/alertDispatcher";
import AlertPanel from "@cayo/shared/components/Alerts/AlertPanel";
import { IAlertItem, AlertPanelProps } from "@cayo/shared/components/Alerts/types";
import { appSignals } from "@cayo/shared/components/App/utils";
import { showMessage } from "@cayo/shared/components/App/utils/showMessage";
import { buildResultDialogScheme } from "@cayo/shared/components/AppRoots/ResultDialogScheme/buildResultDialogScheme";
import { asyncRequestPoolingService } from "@cayo/shared/services/asyncRequestPooling.service";
import { NotificationAction } from "@cayo/shared/services/notifications";
import { objectUtils } from "@cayo/shared/utils/object";
import { getLatestCommandName } from "../../components/CommandBar/controller";

const shouldRemove = (_url: string, data: any, response: any, completeText: string) => {
  const isCompleted = response?.status === "completed";
  const opStatus = response?.operation?.status;

  if (isCompleted) {
    const newAlertItem = {
      id: response.id,
      subject:
        getRuleName(response) || response?.message || data.message || "TODO: Unknown operation",
      severity: opStatus ?? response.status ?? "success",
      message: opStatus === "error" ? response?.operation?.message : completeText,
    } as IAlertItem;

    alertDispatcher.send("systemAlerts", {
      action: NotificationAction.Modify,
      newItems: [newAlertItem],
    });

    alertDispatcher.send("popupAlerts", {
      action: NotificationAction.Modify,
      newItems: [newAlertItem],
      buttonId: "systemAlerts",
    } as IUpdateNewAlerts);
  }

  return isCompleted;
};

const log = logger.getLogger("AdminAlertPanel");

type AlertPanelTexts = {
  operationComplete: string;
  operationInitiated: string;
};

type AdminAlertPanelProps = AlertPanelProps & {
  asyncRequestPollIntervalMs: number;
  texts: AlertPanelTexts;
};

const AdminAlertPanel: FC<AdminAlertPanelProps> = (props) => {
  const { asyncRequestPollIntervalMs, texts } = props;
  const poolManager = useMemo(() => {
    const m = new UrlPollingManager([], asyncRequestPollIntervalMs, (url, data, response) =>
      shouldRemove(url, data, response, texts.operationComplete)
    );
    m.start();
    return m;
  }, [asyncRequestPollIntervalMs]);

  useEffect(() => {
    return () => {
      poolManager.stop();
    };
  }, [poolManager]);

  useEffect(() => {
    return asyncRequestPoolingService.subscribe("startPooling", (data) => {
      if (data?.requestId) {
        const url = `v1.0/requests/${data.requestId}`;
        poolManager.addUrl(url, data);

        alertDispatcher.send("systemAlerts", {
          action: NotificationAction.Modify,
          newItems: [
            {
              id: data.requestId,
              subject: data?.message || getLatestCommandName() || "TODO: Unknown operation",
              severity: "informational",
              message: texts.operationInitiated,
            } as IAlertItem,
          ],
        });

        log.debug("start pooling", url);
      }
    });
  }, []);

  return (
    <AlertPanel
      {...props}
      processResponseFunction={processResponseFunction}
      onItemClick={(item) =>
        (item as any).operation !== null && (item as any).operation !== undefined
          ? onAlertItemClick(item)
          : undefined
      }
    />
  );
};

function getRuleName(data: any): string | null {
  if (data?.initialRuleName) {
    return data.initialRuleName;
  }
  if (data.operation?.results?.length > 0) {
    return data.operation.results[0].RuleName || null;
  }
  return null;
}

function getFirstErrorResult(data: any): string | undefined {
  if (data.operation?.status !== "completed") {
    return data.operation?.message;
  }
  return undefined;
}

const processResponseFunction = (response: any[]) => {
  response.forEach((item) => {
    const alertItem = item as IAlertItem;
    alertItem.subject = getRuleName(item) ?? "TODO: Unknown operation";
    alertItem.lastModifiedDateTime = item.completedAt || item.startedAt;
    alertItem.message = getFirstErrorResult(item);
    alertItem.severity = requestStatusToSeverity(item.operation?.status || item.status);
    alertItem.disabled = item.operation === null || item.operation === undefined;
  });
};

const onAlertItemClick = (item: any) => {
  // Handle the alert item click here
  const { lastModifiedDateTime, operation } = item;
  const operationResults = operation?.results;

  if (operationResults?.length) {
    const results = objectUtils.convertKeysToCamelCase(operationResults);
    const resultScheme = buildResultDialogScheme(results);

    appSignals.send("showForm", {
      form: {
        ...resultScheme,
        lastModifiedDateTime: builtinFormatters.dateTime(lastModifiedDateTime) ?? "",
      },
    } as any);
  } else {
    showMessage("No results found for this alert item.", "informational");
  }
};

export default AdminAlertPanel;
